import React, { createContext, useEffect, useState, PropsWithChildren } from "react";
import type { LogoLocation } from '../types/enum';

const LOCAL_STORAGE_KEY = "votingsiteapp-global-state";

function hasAutoLoginParameters(): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    const params = new Map<string, string>();
    new URLSearchParams(window.location.search).forEach((value, key) => {
        const normalizedKey = key.toLowerCase();
        if (!params.has(normalizedKey)) {
            params.set(normalizedKey, value);
        }
    });
    return Boolean(params.get("cn") && params.get("sc") && params.get("pt") === "1");
}

/** 1) Describe your global state shape */
export interface GlobalState {
    controlNumber: string,
    securityCode: string,
    workOrder: string,
    selectedControlNumber: string,
    selectedSecurityCode: string,
    selectedBallotLabel: string,
    asid: number,
    cardCount: number,
    cardNumber: number,
    allowNAAcess: boolean,
    voteStr: string,
    materialAlias: string,
    isLoginPage: boolean,
    isAutoLoginInProgress: boolean,
    isAttendMeeting: boolean,
    isFromFooter: boolean,
    isFidelity: boolean,
    isAccountVerified: boolean,
    showNoticeAccessPage: boolean,
    showLegalPage: boolean,
    isBeneficialJob: boolean,
    isChatAvailable: boolean,  
    ipAddress: string;
    accountFirstName: string;
    callCenterPhoneNumber?: string;

    fundLogo1: string;
    fundLogo2: string;
    fundLogoLocation: LogoLocation;
    logoBackLink1: string;
    logoBackLink2: string;
    logoReportHeader: string;
}

/** 2) Context value type: state + updater */
export interface GlobalContextValue {
  globalState: GlobalState;
  updateGlobal: <K extends keyof GlobalState>(key: K, value: GlobalState[K]) => void;
}

/** 3) Safe default (no-op updater) so createContext has a value */
const defaultState: GlobalState = {
    controlNumber: '',
    securityCode: '',
    workOrder: '',
    selectedControlNumber: '',
    selectedSecurityCode: '',
    selectedBallotLabel: '',
    asid: 0,
    cardCount: 0,
    cardNumber: 0,
    allowNAAcess: false,
    voteStr: '',
    materialAlias: '',
    isLoginPage: true,
    isAutoLoginInProgress: false,
    isAttendMeeting: false,
    isFromFooter: false,
    isFidelity: false,
    isAccountVerified: false,
    showNoticeAccessPage: false,
    showLegalPage: false,
    isBeneficialJob: false,
    isChatAvailable: false,
    ipAddress: '',
    accountFirstName: '',
    callCenterPhoneNumber: '',

    fundLogo1: "",
    fundLogo2: "",
    fundLogoLocation: 'C',
    logoBackLink1: "",
    logoBackLink2: "",
    logoReportHeader: "",
};

export const GlobalContext = createContext<GlobalContextValue>({
  globalState: defaultState,
  updateGlobal: () => {
    /* no-op default */
  },
});

/** 4) Provider props */
export type GlobalProviderProps = PropsWithChildren;

/** 5) Helper to get default state (typed) */
function getDefaultState(): GlobalState {
  return { ...defaultState, isAutoLoginInProgress: hasAutoLoginParameters() };
}

/** 6) Parse persisted state safely and narrowly type the result */
function loadFromSessionStorage(): GlobalState {
  try {
    const stored = sessionStorage.getItem(LOCAL_STORAGE_KEY);
    if (!stored) return getDefaultState();

    const parsed = JSON.parse(stored) as Partial<GlobalState>;
    // Guard to ensure all required keys exist and are strings
    return {
        controlNumber: parsed.controlNumber ?? "",
        securityCode: parsed.securityCode ?? "",
        workOrder: parsed.workOrder ?? "",
        selectedControlNumber: parsed.selectedControlNumber ?? "",
        selectedSecurityCode: parsed.selectedSecurityCode ?? "",
        selectedBallotLabel: parsed.selectedBallotLabel ?? "",
        asid: parsed.asid ?? 0,
        cardCount: parsed.cardCount ?? 0,
        cardNumber: parsed.cardNumber ?? 0,
        allowNAAcess: parsed.allowNAAcess ?? false,
        voteStr: parsed.voteStr ?? "",
        materialAlias: parsed.materialAlias ?? "",
        isLoginPage: parsed.isLoginPage ?? true,
        isAutoLoginInProgress: hasAutoLoginParameters(),
        isAttendMeeting: parsed.isAttendMeeting ?? false,
        isFromFooter: parsed.isFromFooter ?? false,
        isFidelity: parsed.isFidelity ?? false,
        isAccountVerified: parsed.isAccountVerified ?? false,
        showNoticeAccessPage: parsed.showNoticeAccessPage ?? false,
        showLegalPage: parsed.showLegalPage ?? false,
        isBeneficialJob: parsed.isBeneficialJob ?? false,
        isChatAvailable: parsed.isChatAvailable ?? false,
        ipAddress: parsed.ipAddress ?? "",
        accountFirstName: parsed.accountFirstName ?? "",
        callCenterPhoneNumber: parsed.callCenterPhoneNumber ?? "",

        fundLogo1: parsed.fundLogo1 ?? "",
        fundLogo2: parsed.fundLogo2 ?? "",
        fundLogoLocation: parsed.fundLogoLocation ?? 'C',
        logoBackLink1: parsed.logoBackLink1 ?? "",
        logoBackLink2: parsed.logoBackLink2 ?? "",
        logoReportHeader: parsed.logoReportHeader ?? "",
    };
  } catch (e) {
    console.error("Failed to parse global state from sessionStorage:", e);
    return getDefaultState();
  }
}

/** 7) Provider with strongly typed updateGlobal */
export const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => {
  const [globalState, setGlobalState] = useState<GlobalState>(() => loadFromSessionStorage());

  useEffect(() => {
    try {
      sessionStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(globalState));
    } catch (e) {
      console.error("Failed to save global state to sessionStorage:", e);
    }
  }, [globalState]);

  const updateGlobal = <K extends keyof GlobalState>(key: K, value: GlobalState[K]) => {
    setGlobalState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <GlobalContext.Provider value={{ globalState, updateGlobal }}>
      {children}
    </GlobalContext.Provider>
  );
};